import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock, LogOut, CreditCard } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { toast } from "sonner";

export default function AwaitingActivation() {
  const { user, logout } = useAuth();
  const org = user?.organization || {};
  const [paying, setPaying] = useState(false);

  const paymentPending =
    user?.commercial_state === "PAYMENT_PENDING" ||
    user?.subscription?.payment_status === "PENDING";

  const startPayment = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/paystack/initialize", {
        callback_url: `${window.location.origin}/app/billing`,
      });
      const url = data?.authorization_url || data?.authorization?.authorization_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Could not start payment. Please try again or contact info@epd-nigeria.com.");
        setPaying(false);
      }
    } catch (e) {
      toast.error(apiError(e));
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <BrandMark size={32} />
            <span className="font-display font-semibold">EPD Nigeria</span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="aa-logout"><LogOut className="h-4 w-4 mr-1.5" /> Sign out</Button>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="bg-white border rounded-xl p-8 max-w-md text-center" data-testid="awaiting-activation">
          <div className="h-12 w-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto">
            {paymentPending ? <CreditCard className="h-6 w-6 text-amber-600" /> : <Clock className="h-6 w-6 text-amber-600" />}
          </div>
          <h1 className="font-display text-2xl font-semibold mt-4">
            {paymentPending ? "Complete your subscription payment" : "Workspace awaiting activation"}
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            {paymentPending
              ? "Your email is verified. Complete payment to activate your EPD Nigeria workspace."
              : "Thank you for registering. Our team is reviewing your registration and will confirm activation shortly."}
          </p>
          <div className="mt-4 text-sm rounded-lg border bg-slate-50 p-3 text-left inline-block">
            <div><span className="text-slate-400">Organisation:</span> <b>{org.legal_name}</b></div>
            <div><span className="text-slate-400">Plan:</span> {user?.plan === "Monthly" ? "Enterprise Monthly" : user?.plan === "Annual" ? "Enterprise Annual" : (org.plan || "—")}</div>
            <div><span className="text-slate-400">Status:</span> {user?.subscription_status || "Pending"}</div>
            <div><span className="text-slate-400">Email verified:</span> {user?.email_verified ? "Yes" : "Pending — check your inbox"}</div>
            {user?.subscription && (
              <>
                <div><span className="text-slate-400">Named users:</span> {user.subscription.contracted_named_users}</div>
                <div><span className="text-slate-400">Subscription value:</span> ₦{Number(user.subscription.subtotal || 0).toLocaleString()}</div>
                <div data-testid="aa-payment"><span className="text-slate-400">Payment:</span> {user.subscription.payment_status === "PENDING" ? "Payment pending" : user.subscription.payment_status === "UNPAID" ? "Unpaid" : user.subscription.payment_status}</div>
              </>
            )}
          </div>
          {paymentPending && (
            <div className="mt-5 space-y-2.5" data-testid="aa-payment-options">
              <Button className="w-full" onClick={startPayment} disabled={paying} data-testid="aa-proceed-payment-btn">
                <CreditCard className="h-4 w-4 mr-2" />
                {paying ? "Redirecting to Paystack…" : "Proceed to payment"}
              </Button>
              <p className="text-xs text-slate-500 text-center" data-testid="aa-payment-support-text">
                Pay securely with Paystack using any payment method available to you.
              </p>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            {paymentPending
              ? <>Payment is processed securely by Paystack. Need help? <a href="mailto:info@epd-nigeria.com" className="hover:underline">Contact EPD Nigeria</a>.</>
              : <>You'll receive an email when your workspace is active. Need help? <a href="mailto:info@epd-nigeria.com" className="hover:underline">Contact EPD Nigeria</a>.</>}
          </p>
        </div>
      </div>
    </div>
  );
}
